/**
 * Which player(s) THIS device plays for, so the end of the game can offer them their own poster (Indra, 2026-09-27), and which
 * player it should pick by default when a round is submitted. Three things tell us, best first:
 *  1. the winners of the rounds this device SUBMITTED (a guest phone: the rounds it sent; the host phone: the rounds it entered
 *     itself, not the ones it applied from a guest) — by default each player submits their own;
 *  2. the "This is me" tick on the winner field, which also makes that player the default winner next time;
 *  3. in a leaderboard game from a sign-in table, the nickname the phone signed in with (this is the only thing that identifies a
 *     player who never won a round).
 * When a phone has helped someone else record a round it knows MORE than one player, and the end of the game asks which poster to show.
 *
 * Keys are `game:<game id>` on the host's device and `share:<guest link token>` on a guest's. Storage can be unavailable (private
 * windows), so every access is guarded: a failure just means no offer / no default, never an error.
 */

const VIEWER_PREFIX = 'mahjong-scorer:viewer:';
const SEEN_PREFIX = 'mahjong-scorer:posterOfferSeen:';
const MINE_PREFIX = 'mahjong-scorer:myPlayers:';
const DEFAULT_WINNER_PREFIX = 'mahjong-scorer:defaultWinner:';

export const gameViewerKey = (gameId: string): string => `game:${gameId}`;
export const shareViewerKey = (shareToken: string): string => `share:${shareToken}`;

/** The nickname this phone signed in with at a leaderboard table. */
export function rememberViewer(key: string, nickname: string): void {
  try {
    localStorage.setItem(VIEWER_PREFIX + key, nickname);
  } catch {
    // No offer later; nothing else is affected.
  }
}

export function getViewer(key: string): string | undefined {
  try {
    return localStorage.getItem(VIEWER_PREFIX + key) ?? undefined;
  } catch {
    return undefined;
  }
}

/** A player id whose round this device submitted (or ticked "This is me" for). */
export function rememberMyPlayer(key: string, playerId: string): void {
  try {
    const mine = getMyPlayers(key);
    if (mine.includes(playerId)) return;
    localStorage.setItem(MINE_PREFIX + key, JSON.stringify([...mine, playerId]));
  } catch {
    // Not remembered: the offer just knows less.
  }
}

export function getMyPlayers(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(MINE_PREFIX + key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** The winner to pick by default on this device ("This is me" ticked), or undefined. */
export function getDefaultWinner(key: string): string | undefined {
  try {
    return localStorage.getItem(DEFAULT_WINNER_PREFIX + key) ?? undefined;
  } catch {
    return undefined;
  }
}

export function setDefaultWinner(key: string, playerId: string | undefined): void {
  try {
    if (playerId === undefined) localStorage.removeItem(DEFAULT_WINNER_PREFIX + key);
    else {
      localStorage.setItem(DEFAULT_WINNER_PREFIX + key, playerId);
      rememberMyPlayer(key, playerId); // ticking "This is me" is the clearest sign of whose phone this is
    }
  } catch {
    // Not remembered: the winner just has to be picked again.
  }
}

/** The offer is made once per game on a device, so going back to the results later doesn't nag. */
export function posterOfferSeen(key: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + key) === '1';
  } catch {
    return true; // cannot remember that it was shown, so do not show it at all
  }
}

/** The game is being played again (the host went "back to this game"): the next time it ends, the offer is made again — like the confetti. */
export function clearPosterOfferSeen(key: string): void {
  try {
    localStorage.removeItem(SEEN_PREFIX + key);
  } catch {
    // Ignored: at worst the offer is not repeated.
  }
}

export function markPosterOfferSeen(key: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + key, '1');
  } catch {
    // Ignored: posterOfferSeen already answers "seen" when storage is unavailable.
  }
}
